import { Link } from 'react-router-dom';
import { footerLinks } from '@/mocks/homeData';
import { useSiteData } from '@/contexts/SiteDataContext';

const LOGO_URL = 'https://static.readdy.ai/image/5b0394d63ea67e242d80d6c69b47f9d5/248fb4068954f3ccbfda9e34faec5a86.png';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { data } = useSiteData();
  const contact = data.contact;
  const org = data.org;
  const socialLinks = data.socialLinks;

  return (
    <footer style={{ backgroundColor: 'oklch(0.15 0.09 264)' }} className="text-white">
      {/* Tricolor top accent bar */}
      <div className="flex h-1">
        <div className="flex-1 bg-accent-500"></div>
        <div className="flex-1 bg-white/30"></div>
        <div className="flex-1 bg-secondary-500"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-14 pb-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Brand column with actual logo */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-5">
              <div className="relative w-20 h-20 flex-shrink-0">
                <div className="absolute inset-0 rounded-full ring-2 ring-accent-400/40 ring-offset-2 ring-offset-transparent"></div>
                <img
                  src={org.logoUrl}
                  alt="Happy Faces Hyderabad Rural Development Welfare Society"
                  className="w-full h-full rounded-full object-cover border-2 border-white/20"
                />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-white leading-tight">
                  {org.name.split(' ').slice(0, 2).join(' ')}
                </h3>
                <h3 className="font-heading text-lg font-bold text-white leading-tight">
                  {org.name.split(' ').slice(2).join(' ')}
                </h3>
                <p className="text-accent-400 text-xs font-medium mt-0.5 leading-snug">
                  {org.tagline}
                </p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5 max-w-sm">
              {org.description}
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 hover:text-primary-900 transition-all duration-300"
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-accent-400 uppercase tracking-widest mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-accent-400 transition-colors duration-200 flex items-center gap-1.5"
                  >
                    <i className="ri-arrow-right-s-line text-accent-500/60 text-xs"></i>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-accent-400 uppercase tracking-widest mb-4">Get Involved</h4>
            <ul className="space-y-2.5">
              {footerLinks.getInvolved.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-accent-400 transition-colors duration-200 flex items-center gap-1.5"
                  >
                    <i className="ri-arrow-right-s-line text-accent-500/60 text-xs"></i>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Activities highlight */}
            <div className="mt-6 p-3 rounded-xl border border-secondary-500/30 bg-secondary-900/20">
              <p className="text-secondary-400 text-xs font-semibold uppercase tracking-widest mb-2">Our Activities</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="px-2 py-0.5 rounded-full bg-secondary-500/20 text-secondary-300 text-xs font-medium">Food Distribution</span>
                <span className="px-2 py-0.5 rounded-full bg-accent-500/20 text-accent-300 text-xs font-medium">Blanket Distribution</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-accent-400 uppercase tracking-widest mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-2-fill text-accent-400 mt-0.5 text-sm flex-shrink-0"></i>
                <span className="text-white/60 text-sm leading-relaxed">{contact.address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-phone-fill text-accent-400 text-sm flex-shrink-0"></i>
                <a href={`tel:${contact.phone}`} className="text-white/60 text-sm hover:text-accent-400 transition-colors">
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-mail-fill text-accent-400 text-sm mt-0.5 flex-shrink-0"></i>
                <a href={`mailto:${contact.email}`} className="text-white/60 text-sm hover:text-accent-400 transition-colors break-all">
                  {contact.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-xs text-center sm:text-left">
              &copy; {currentYear} Happy Faces Hyderabad — Rural Development Welfare Society. All rights reserved. Registered under the Societies Registration Act.
            </p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {footerLinks.resources.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-white/40 text-xs hover:text-accent-400 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}